<script setup lang="ts">
import {
  computed,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  watchEffect,
} from "vue";
import { locale } from "../locale";
import HelpAlert from "./HelpAlert.vue";
import SimpleBar from "simplebar";
import { fetcher } from "../api/fetcher";
import { roomState } from "../states/room";
import { router } from "../router";
import ChatItemIcon from "./icons/ChatItemIcon.vue";
import CopyRecordLink from "./icons/CopyRecordLink.vue";
import ClearChatIconSmall from "./icons/ClearChatIconSmall.vue";
import { ClearChatModal, closeModal } from "../utils/programmingModals";
import { clearChatHistory } from "../states/chatHistoryState";
import { formattedDate } from "../utils/date";
import DownloadRecord from "./icons/DownloadRecord.vue";
import RecordItemIcon from "./icons/RecordItemIcon.vue";
import ToolTip from "./ToolTip.vue";
import { isSelfAdmin } from "../states/adminsState";
import DownloadRecordSummary from "./icons/DownloadRecordSummary.vue";
import DownloadRecordSummarySpinner from "./icons/DownloadRecordSummarySpinner.vue";
import { featuresState, updateFeaturesState } from "../states/featuresState";
import RecordSummary from "./icons/RecordSummary.vue";
import { build_settings } from "../assets/custom/settings";

updateFeaturesState();
const links = ref<
  {
    type: string;
    payload: string;
    date: string;
    expired: boolean;
    summary: "ready" | "not-ready" | "empty";
  }[]
>([]);
let lastTimer: number | NodeJS.Timeout | null = null;
const readyAllSummaries = ref(false);
const loadingChat = ref(true);
const loadingRecords = ref(true);
const loading = computed(() => loadingChat.value || loadingRecords.value);
const scroll = ref<HTMLElement>();
watchEffect(() => {
  if (scroll.value) {
    new SimpleBar(scroll.value, { autoHide: false });
  }
});

if (isSelfAdmin.value) {
  getLinks();
}
const visibilitychangeHook = () => {
  if (lastTimer) {
    clearTimeout(lastTimer);
  }
  if (loadingRecords.value) {
    getLinks();
  } else if (!loadingRecords.value && !readyAllSummaries.value) {
    updateSummaryStatuses();
  }
};
onMounted(() => {
  document.addEventListener("visibilitychange", visibilitychangeHook);
});
onUnmounted(() => {
  document.removeEventListener("visibilitychange", visibilitychangeHook);
});

function updateSummaryStatuses() {
  lastTimer = setTimeout(async () => {
    const res = (await fetcher.get({
      path: `/records/summaries/${roomState.id}`,
    })) as { state: "ready" | "not-ready"; linkId: string }[];
    let allReady = true;
    for (const item of res) {
      const link = links.value.find((l) => l.payload === item.linkId);
      if (link) {
        link.summary = item.state;
      }
      allReady = allReady && item.state === "ready";
    }
    if (!allReady) {
      readyAllSummaries.value = false;
      updateSummaryStatuses();
    } else {
      readyAllSummaries.value = true;
    }
  }, 5000);
}

async function getLinks() {
  if (isSelfAdmin.value && loadingRecords.value) {
    try {
      const res = (await fetcher.get({
        path: `/records/${roomState.id}`,
      })) as typeof links.value;
      loadingRecords.value = false;
      links.value = res;
      if (
        res.length > 0 &&
        res.reduce((acc, val) => {
          return acc || val.summary === "not-ready";
        }, false)
      ) {
        updateSummaryStatuses();
      } else {
        readyAllSummaries.value = true;
      }
    } catch {
      loadingRecords.value = true;
      lastTimer = setTimeout(() => {
        getLinks();
      }, 5000);
    }
  }
}

const linkData = computed(() => {
  return links.value.map((link) => {
    const date = formattedDate(link.date);
    return {
      type: link.type,
      link: link.payload,
      filename: `${date} - ${locale.linkNames.record}`,
      summaryName: `${date} - ${locale.linkNames.summary}`,
      expired: link.expired,
      summary: link.summary,
    };
  });
});
const copybtns = ref<(typeof CopyRecordLink)[]>([]);
const resetAnother = (id: number) => {
  copybtns.value.forEach((e, idx) => id !== idx && e.reset());
};

const copy_link = (linkId: string) => {
  if (navigator.clipboard.writeText) {
    navigator.clipboard.writeText(
      window.location.origin + `/api/records/${linkId}/view`,
    );
  }
};
const downloadRec = (linkId: string) => {
  const a = document.createElement("a");
  a.href = window.location.origin + `/api/records/${linkId}/get`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

const downloadSummary = (linkId: string) => {
  const a = document.createElement("a");
  a.href = window.location.origin + `/api/records/summary/${linkId}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

const data = reactive({
  historyId: "",
  isMessages: false,
});

fetcher
  .get({
    path: `/chat/chat-history-id/${router.currentRoute.value.params.roomId}`,
  })
  .then((res: { historyId: string; isMessages: boolean }) => {
    Object.assign(data, res);
    loadingChat.value = false;
  });
const openHistoryChat = async () => {
  router.push({
    name: "chat-history",
    params: {
      roomId: data.historyId,
    },
  });
};
const copy_link_chat = () => {
  if (navigator.clipboard.writeText) {
    navigator.clipboard.writeText(
      window.location.origin + `/chat-history/${data.historyId}`,
    );
  }
};
const clearChatHandler = async () => {
  ClearChatModal(async () => {
    await clearChatHistory().catch(console.log);
    closeModal();
  });
};
const btn = ref();
const openSummary = (link: (typeof linkData.value)[number]) => {
  router.push({
    name: "summary-view",
    params: {
      summaryId: link.link,
    },
  });
};
</script>

<template>
  <div class="chat-record-block">
    <div class="title">{{ locale.chatRecordBlock.title }}</div>
    <template v-if="loading">
      <div class="loading">
        <svg
          class="spinner"
          width="48"
          height="48"
          viewBox="0 0 48 48"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M42 24C42 28.1644 40.5561 32.1999 37.9142 35.4191C35.2723 38.6382 31.596 40.8417 27.5116 41.6541C23.4273 42.4665 19.1875 41.8376 15.5149 39.8746C11.8422 37.9115 8.96382 34.7357 7.37017 30.8883C5.77653 27.0409 5.56622 22.76 6.77508 18.7749C7.98394 14.7898 10.5372 11.3472 13.9997 9.03357C17.4623 6.71996 21.62 5.67852 25.7643 6.0867C28.3321 6.33961 30.7963 7.13931 33 8.41155"
            stroke="#A4A4A4"
            stroke-width="2.5"
          />
        </svg>
        <div class="loading-text">{{ locale.chatRecordBlock.loadingText }}</div>
      </div>
    </template>
    <template v-else-if="!loading">
      <div ref="scroll" class="list">
        <div class="list-wrapper-inner">
          <div class="chat-item">
            <ChatItemIcon class="icon" />
            <span class="link-text" @click="openHistoryChat">{{
              locale.linkNames.chat
            }}</span>
            <CopyRecordLink
              class="copylink"
              @click="copy_link_chat"
              :reset-another="() => resetAnother(-1)"
            />
            <ClearChatIconSmall
              class="clear-chat-ico"
              @click="clearChatHandler"
              ref="btn"
            />
            <ToolTip placement="auto" :reference="btn?.svg">{{
              locale.chat.clearChatTooltip
            }}</ToolTip>
          </div>
          <template v-for="(link, id) in linkData" :key="id">
            <div v-if="!link.expired" class="record-item">
              <RecordItemIcon class="icon" />
              <a
                :href="`/api/records/${link.link}/view`"
                target="_blank"
                :filename="link.filename"
                >{{ link.filename }}</a>
              <CopyRecordLink
                v-if="!link.expired"
                ref="copybtns"
                class="copylink"
                @click="() => copy_link(link.link)"
                :reset-another="() => resetAnother(id)"
              />
              <DownloadRecord
                v-if="!link.expired"
                class="download-rec-ico"
                ref="downloadbtns"
                @click="() => downloadRec(link.link)"
              />
            </div>
            <div v-if="featuresState.summaryShow && link.summary !== 'empty'" class="record-item">
              <RecordSummary class="icon" />
              <a
                v-if="link.summary === 'ready'"
                @click="() => openSummary(link)"
                >{{ link.summaryName }}</a
              >
              <span v-else class="expired-record">{{ link.summaryName }}</span>
              <DownloadRecordSummary
                v-if="link.summary === 'ready'"
                class="download-rec-summary-ico"
                ref="downloadSummarybtns"
                @click="() => downloadSummary(link.link)"
                :style="{
                  'margin-left': !link.expired ? '4px' : '0px',
                }"
              />
              <DownloadRecordSummarySpinner
                v-else-if="link.summary === 'not-ready'"
                class="download-rec-summary-ico"
              />
            </div>
          </template>
        </div>
      </div>
      <HelpAlert v-if="linkData.length > 0 && build_settings.showExpireLinkInfoPanel" class="alert"
        >{{ locale.record7days }}
      </HelpAlert>
    </template>
  </div>
</template>
<style lang="scss" scoped>
.chat-record-block {
  flex-shrink: 0;
  width: 450px;
  padding: 24px;
  background: #ffffff;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.copylink {
  margin-right: 4px;
}

.chat-item {
  display: flex;
  justify-content: space-between;
  padding-right: 8px;
}

.record-item {
  display: flex;
  justify-content: space-between;
  padding-right: 8px;
}

.clear-chat-ico {
  cursor: pointer;
}

.list-wrapper-inner {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.link-text,
a {
  flex-grow: 1;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
  font-family: Open Sans;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  color: var(--rec-link-color);
  text-decoration: none;
  margin-right: 8px;
}

.expired-record {
  flex-grow: 1;
  cursor: default;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
  font-family: Open Sans;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  color: #292929;
  text-decoration: none;
  margin-right: 8px;
}

.icon {
  margin-right: 8px;
}

.icon,
.copylink,
.clear-chat-ico,
.download-rec-ico,
.download-rec-summary-ico {
  flex-shrink: 0;
}

.loading {
  height: 100px;
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 12px;
}

.loading-text {
  font-family: Open Sans;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  text-align: center;
  color: #747474;
}

.spinner {
  animation: rotation 1s linear infinite;
}

@keyframes rotation {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.alert {
  margin: 0 -6.5px;
}

.list {
  height: 100px;
  flex-grow: 1;
  margin-right: -8px;
}

.title {
  height: 24px;
  width: 100%;
  text-align: center;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
  color: var(--greeting-title-color);
}
</style>
<style>
.chat-record-block .simplebar-vertical .simplebar-scrollbar::before {
  background-color: rgba(116, 116, 116, 0.55);
  width: 4px;
}
</style>
